import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DialogService } from 'primeng/dynamicdialog';
import { catchError, exhaustMap, filter, map, mergeMap, of, tap, withLatestFrom } from 'rxjs';

import { AddQuillTemplateGQL, QuillTemplateLocation, UpdateQuillTemplateGQL, DeleteQuillTemplateGQL,QuillTemplate } from '../../../generated/graphql.generated';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';

import { MutateTemplateComponent } from '../mutate-template/mutate-template.component';

import { TemplatesActions , templatesFeature } from './templates.store';


export const addTemplate$ = createEffect(() => {
  const actions$ = inject(Actions);
  const addQuillTemplatesGQL = inject(AddQuillTemplateGQL);
  const notify = inject(FreyaNotificationsService);
  return actions$.pipe(
    ofType(TemplatesActions.templateCreationRequested),
    mergeMap(({ input }) =>
      addQuillTemplatesGQL.mutate({input}).pipe(
        map(({ data }) => {
          if (data?.addQuillTemplate?.template) {
            notify.success('Template added successfully.');
            return TemplatesActions.templateCreated({ template: data.addQuillTemplate.template });
          }
          return TemplatesActions.templateUpdateFailed({ error: new Error('No template returned') });
        }),
        catchError((error) => {
          notify.error('Failed to add template.');
          return of(TemplatesActions.templateCreationFailed({ error }));
        })
      )
    )
  );
}, { functional: true });

export const updateTemplate$ = createEffect(() => {
  const actions$ = inject(Actions);
  const updateQuillTemplatesGQL = inject(UpdateQuillTemplateGQL);
  const notify = inject(FreyaNotificationsService);
  return actions$.pipe(
    ofType(TemplatesActions.templateUpdateRequested),
    mergeMap(({ input }) =>
      updateQuillTemplatesGQL.mutate({input}).pipe(
        map(({ data }) => {
          if (data?.updateQuillTemplate?.templates?.length) {
            notify.success('Template updated successfully.');
            return TemplatesActions.templateUpdated({ template: data.updateQuillTemplate.templates[0] });
          }
          return TemplatesActions.templateUpdateFailed({ error: new Error('No template returned') });
        }),
        catchError((error) => {
          notify.error('Failed to update template.');
          return of(TemplatesActions.templateUpdateFailed({ error }));
        })
      )
    )
  );
}, { functional: true });

export const deleteTemplate$ = createEffect(() => {
  const actions$ = inject(Actions);
  const deleteQuillTemplatesGQL = inject(DeleteQuillTemplateGQL);
  const notify = inject(FreyaNotificationsService);
  return actions$.pipe(
    ofType(TemplatesActions.templateDeletionRequested),
    mergeMap(({ id }) =>
      deleteQuillTemplatesGQL.mutate({ input: { ids: [id] } }).pipe(
        map(({ data }) => {
          if (data?.deleteQuillTemplate) {
            notify.success('Template deleted successfully.');
            return TemplatesActions.templateDeleted({ id });
          }
          return TemplatesActions.templateDeletionFailed({ error: new Error('No template deleted') });
        }),
        catchError((error) => {
          notify.error('Failed to delete template.');
          return of(TemplatesActions.templateDeletionFailed({ error }));
        })
      )
    )
  );
}, { functional: true });

export const openAddTemplateDialog$ = createEffect(() => {
  const actions$ = inject(Actions);
  const dialogService = inject(DialogService);
  return actions$.pipe(
    ofType(TemplatesActions.createTemplateButtonClicked),
    mergeMap(({location}) => {
      const ref = dialogService.open(MutateTemplateComponent, {
        header: 'Add New Template',
        width: '50%',
        data: { template: {
            location,
        } },
      });
      return ref.onClose.pipe(
        filter(Boolean),
        map((newTemplate: QuillTemplate) =>
          TemplatesActions.templateCreationRequested({
            input: {
              name: newTemplate.name,
              contents: JSON.stringify(newTemplate.contents),
              location: newTemplate.location,
              subjectContents: newTemplate.subjectContents ? JSON.stringify(newTemplate.subjectContents) : null,
            },
          })
        )
      );
    })
  );
}, { functional: true });

export const openEditTemplateDialog$ = createEffect(() => {
  const actions$ = inject(Actions);
  const dialogService = inject(DialogService);
  return actions$.pipe(
    ofType(TemplatesActions.editTemplateButtonClicked),
    mergeMap(({ template }) => {
      const ref = dialogService.open(MutateTemplateComponent, {
        header: 'Edit Template',
        width: '50%',
        data: { template },
      });
      return ref.onClose.pipe(
        filter(Boolean),
        map((updatedTemplate: QuillTemplate) =>
          TemplatesActions.templateUpdateRequested({
            input: {
              ids: [updatedTemplate.id],
              update: {
                name: updatedTemplate.name,
                contents: JSON.stringify(updatedTemplate.contents),
                location: updatedTemplate.location,
                subjectContents: updatedTemplate.subjectContents ? JSON.stringify(updatedTemplate.subjectContents) : null,
              },
            },
          })
        )
      );
    })
  );
}, { functional: true });

export const saveTemplateEdits$ = createEffect(() => {
    const actions$ = inject(Actions);
    const store = inject(Store);

    return actions$.pipe(
        ofType(TemplatesActions.saveButtonClicked),
        withLatestFrom(store.select(templatesFeature.selectTemplateContentEdits)),
        filter(([{template}, templateContentEdits]) =>  Boolean(templateContentEdits[template.id])),
        exhaustMap(([{ template }, templateContentEdits]) => {
            const {contents, subjectContents} = templateContentEdits[template.id];

            return of(TemplatesActions.templateUpdateRequested({
                input: {
                    ids: [template.id],
                    update: {
                        location: template.location,
                        ...(contents && {contents: JSON.stringify(contents)}),
                        ...(subjectContents && {subjectContents: JSON.stringify(subjectContents)})
                    }
                }
            }));
        })
    );
}, { functional: true });
